import Vue from 'vue'

import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../../../layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'

/* Plugins */

import nuxt_plugin_plugin_6e56adae from 'nuxt_plugin_plugin_6e56adae' // Source: ./components/plugin.js (mode: 'all')
import nuxt_plugin_plugin_691db600 from 'nuxt_plugin_plugin_691db600' // Source: ./vuetify/plugin.js (mode: 'all')
import nuxt_plugin_pluginrouting_6db5ec82 from 'nuxt_plugin_pluginrouting_6db5ec82' // Source: ./nuxt-i18n/plugin.routing.js (mode: 'all')
import nuxt_plugin_pluginmain_23841e2d from 'nuxt_plugin_pluginmain_23841e2d' // Source: ./nuxt-i18n/plugin.main.js (mode: 'all')
import nuxt_plugin_templatesplugin18e27ade_49963cd5 from 'nuxt_plugin_templatesplugin18e27ade_49963cd5' // Source: ./templates.plugin.18e27ade.js (mode: 'all')
import nuxt_plugin_markdownit_78f7473a from 'nuxt_plugin_markdownit_78f7473a' // Source: ./markdownit.js (mode: 'all')
import nuxt_plugin_workbox_fd2cfc40 from 'nuxt_plugin_workbox_fd2cfc40' // Source: ./workbox.js (mode: 'client')
import nuxt_plugin_metaplugin_0192e8f3 from 'nuxt_plugin_metaplugin_0192e8f3' // Source: ./pwa/meta.plugin.js (mode: 'all')
import nuxt_plugin_iconplugin_063a8e67 from 'nuxt_plugin_iconplugin_063a8e67' // Source: ./pwa/icon.plugin.js (mode: 'all')
import nuxt_plugin_axios_593d6dc4 from 'nuxt_plugin_axios_593d6dc4' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_pluginclient_1f641bac from 'nuxt_plugin_pluginclient_1f641bac' // Source: ./content/plugin.client.js (mode: 'client')
import nuxt_plugin_pluginserver_560763b2 from 'nuxt_plugin_pluginserver_560763b2' // Source: ./content/plugin.server.js (mode: 'server')
import nuxt_plugin_googleanalytics_49d667e2 from 'nuxt_plugin_googleanalytics_49d667e2' // Source: ./google-analytics.js (mode: 'client')
import nuxt_plugin_image_44446abf from 'nuxt_plugin_image_44446abf' // Source: ./image.js (mode: 'all')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root.$options.$nuxt
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

async function createApp(ssrContext, config = {}) {
  const router = await createRouter(ssrContext, config)

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"title":"Damien Robinson","titleTemplate":"%s | Daim","meta":[{"once":true,"name":"charset","hid":"charset","content":"utf-8"},{"once":true,"hid":"viewport","name":"viewport","content":"width=device-width, initial-scale=1"},{"once":true,"name":"version","hid":"version","content":"1.33.0"},{"once":true,"property":"og:title","content":"Damien Robinson","template":"%s | Daim","hid":"og:title"},{"name":"version","content":"1.33.0"}],"link":[{"rel":"icon","type":"image\u002Fx-icon","href":"\u002Ffavicon.ico"}],"noscript":[{"innerHTML":"This website requires JavaScript."}],"style":[],"script":[]},

    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (typeof nuxt_plugin_plugin_6e56adae === 'function') {
    await nuxt_plugin_plugin_6e56adae(app.context, inject)
  }

  if (typeof nuxt_plugin_plugin_691db600 === 'function') {
    await nuxt_plugin_plugin_691db600(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginrouting_6db5ec82 === 'function') {
    await nuxt_plugin_pluginrouting_6db5ec82(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginmain_23841e2d === 'function') {
    await nuxt_plugin_pluginmain_23841e2d(app.context, inject)
  }

  if (typeof nuxt_plugin_templatesplugin18e27ade_49963cd5 === 'function') {
    await nuxt_plugin_templatesplugin18e27ade_49963cd5(app.context, inject)
  }

  if (typeof nuxt_plugin_markdownit_78f7473a === 'function') {
    await nuxt_plugin_markdownit_78f7473a(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_workbox_fd2cfc40 === 'function') {
    await nuxt_plugin_workbox_fd2cfc40(app.context, inject)
  }

  if (typeof nuxt_plugin_metaplugin_0192e8f3 === 'function') {
    await nuxt_plugin_metaplugin_0192e8f3(app.context, inject)
  }

  if (typeof nuxt_plugin_iconplugin_063a8e67 === 'function') {
    await nuxt_plugin_iconplugin_063a8e67(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_593d6dc4 === 'function') {
    await nuxt_plugin_axios_593d6dc4(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_pluginclient_1f641bac === 'function') {
    await nuxt_plugin_pluginclient_1f641bac(app.context, inject)
  }

  if (process.server && typeof nuxt_plugin_pluginserver_560763b2 === 'function') {
    await nuxt_plugin_pluginserver_560763b2(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_googleanalytics_49d667e2 === 'function') {
    await nuxt_plugin_googleanalytics_49d667e2(app.context, inject)
  }

  if (typeof nuxt_plugin_image_44446abf === 'function') {
    await nuxt_plugin_image_44446abf(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    router.push(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    app,
    router
  }
}

export { createApp, NuxtError }
